// src/components/context/CurrencyContext.js

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useAuth } from './AuthContext';

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
    // ⭐ FIX: Added || {} so it safely falls back to an empty object for public visitors
    const { user, loading: authLoading } = useAuth() || {};


    // State for the currently selected/active currency.
    const [currency, setCurrency] = useState(null);
    // State for all available currencies.
    const [availableCurrencies, setAvailableCurrencies] = useState([]);
    // The loading state now also indicates if the context is ready for use.
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Hardcoded list of common currencies for selection.
    const defaultAvailableCurrencies = [
        { code: 'GBP', symbol: '£', decimalPlaces: 2, thousandSeparator: ',', decimalSeparator: '.', formatTemplate: '{symbol}{amount}' },
        { code: 'USD', symbol: '$', decimalPlaces: 2, thousandSeparator: ',', decimalSeparator: '.', formatTemplate: '{symbol}{amount}' },
        { code: 'EUR', symbol: '€', decimalPlaces: 2, thousandSeparator: '.', decimalSeparator: ',', formatTemplate: '{amount}{symbol}' },
        { code: 'CAD', symbol: '$', decimalPlaces: 2, thousandSeparator: ',', decimalSeparator: '.', formatTemplate: '{symbol}{amount}' },
        { code: 'AUD', symbol: '$', decimalPlaces: 2, thousandSeparator: ',', decimalSeparator: '.', formatTemplate: '{symbol}{amount}' },
        { code: 'JPY', symbol: '¥', decimalPlaces: 0, thousandSeparator: ',', decimalSeparator: '.', formatTemplate: '{symbol}{amount}' },
        { code: 'PLN', symbol: 'zł', decimalPlaces: 2, thousandSeparator: ' ', decimalSeparator: ',', formatTemplate: '{amount} {symbol}' },
    ];

    // Memoize the API call function to prevent re-creation and potential infinite loops.
    const fetchCurrencySettings = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/settings');
            const fetchedCurrency = res.data?.companySpecificSettings?.currency;

            if (fetchedCurrency && typeof fetchedCurrency === 'object') {
                setCurrency(fetchedCurrency);
            } else {
                setCurrency(defaultAvailableCurrencies[0]); // Default to GBP
            }
            setAvailableCurrencies(defaultAvailableCurrencies);
        } catch (err) {
            console.error("Failed to fetch currency settings:", err.response?.data || err.message);
            setError(err.response?.data?.message || "Failed to load currency settings.");
            setCurrency(defaultAvailableCurrencies[0]);
            setAvailableCurrencies(defaultAvailableCurrencies);
        } finally {
            setLoading(false);
        }
    }, []);

    // This useEffect hook is the key to managing the loading state correctly.
    useEffect(() => {
        // If authentication is still loading, we should do nothing but wait.
        if (authLoading) {
            return;
        }

        // If a user is present, we fetch their specific settings.
        if (user && user.company?._id) {
            fetchCurrencySettings();
        } else {
            // If no user is logged in, we immediately set a fallback currency and stop loading.
            setCurrency(defaultAvailableCurrencies[0]);
            setAvailableCurrencies(defaultAvailableCurrencies);
            setLoading(false);
        }
    }, [user, authLoading, fetchCurrencySettings]);

    // --- FIX START ---
    // The original `updateCurrency` function was making a redundant and faulty API call.
    // It has been replaced with a simple state setter. The component responsible for saving
    // settings to the server (like SettingsPage.js) should make the API call and then
    // use this function to update the global currency state across the app. This
    // resolves the error and improves performance by removing the unnecessary network request.
    const updateCurrency = useCallback((newCurrencyObject) => {
        if (newCurrencyObject && typeof newCurrencyObject === 'object' && newCurrencyObject.code) {
            setCurrency(newCurrencyObject);
        } else {
            // Log a warning for developers if the currency object is invalid, but don't show an error to the user.
            console.warn("Attempted to update currency with an invalid object:", newCurrencyObject);
        }
    }, []);
    // --- FIX END ---


    const formatCurrency = useCallback((amount) => {
        // If the currency state is not yet set, we use a default.
        if (!currency) {
            return `£${Number(amount)?.toFixed(2) || '0.00'}`;
        }

        if (typeof amount === 'number') {
            const { symbol, decimalPlaces, thousandSeparator, decimalSeparator, formatTemplate, code } = currency;
            const fixedAmount = amount.toFixed(decimalPlaces);
            const parts = fixedAmount.split('.');
            parts[0] = parts[0].replace(new RegExp(`\\B(?=(\\d{3})+(?!\\d))`, 'g'), thousandSeparator);
            const formattedAmount = parts.join(decimalSeparator);

            return formatTemplate
                .replace('{symbol}', symbol)
                .replace('{amount}', formattedAmount)
                .replace('{code}', code);
        }
        return `£${Number(amount)?.toFixed(2) || '0.00'}`;
    }, [currency]);

    return (
        <CurrencyContext.Provider value={{ currency, availableCurrencies, loading, error, updateCurrency, formatCurrency }}>
            {children}
        </CurrencyContext.Provider>
    );
};

export const useCurrency = () => {
    const context = useContext(CurrencyContext);
    if (context === undefined) {
        // ⭐ FIX: Return a safe default instead of a fatal error, matching our Settings fix
        console.warn('useCurrency must be used within a CurrencyProvider. Using fallbacks.');
        return { currency: null, formatCurrency: (amt) => `£${amt}` };
    }
    return context;
};
